<script>
import Nav from '@/components/Nav.vue'
import Footer from '@/components/Footer.vue'
import Hours from '@/components/Hours.vue'
import { businessHours } from '@/util/hours.js'
import { API_URL } from '@/config.js'

export default {
    name: 'Home',
    components: { NavComponent: Nav, FooterComponent: Footer, Hours },
    data() {
        return {
            services: [
                {
                    icon: 'ph-duotone ph-wrench',
                    badgeGreen: false,
                    title: 'Sim Racing Tech Support',
                    desc: "Anyone with a rig can get expert help. Whether you're wrestling with SimHub haptics, dialing in a wheelbase, or setting up iRacing for the first time — we've been there, and we'll get you sorted.",
                    features: ['iRacing, Assetto Corsa, ACC, rFactor 2 setup', 'SimHub dashboards & haptics tuning', 'Wheelbase, pedal & hardware configuration', 'Triple monitor & Nvidia Surround setup', 'Motion platform & bass shaker config'],
                },
                {
                    icon: 'ph-duotone ph-headset',
                    badgeGreen: true,
                    title: 'ShiftOS Platform Support',
                    desc: "You're already a ShiftOS customer — your support is fully covered. We handle onboarding, troubleshooting, bug reports, and feature requests so your venue never misses a lap.",
                    features: ['AnyDesk remote onboarding', 'Steam + Assetto Corsa setup', 'ShiftOS Launcher configuration', 'Ongoing troubleshooting & bug reports', 'Feature requests & customer follow-up'],
                },
                {
                    icon: 'ph-duotone ph-buildings',
                    badgeGreen: true,
                    title: 'Shift Arcade Franchises',
                    desc: 'Dedicated support for Shift Arcade franchisees and SimStation operators. We coordinate onboarding, configure your hardware, and keep your simulators race-ready.',
                    features: ['Franchise onboarding & ShiftOS coordination', 'D-Box & Sigma software configuration', 'Physical hardware support', 'Remote troubleshooting via AnyDesk', 'SimStation setup & ongoing support'],
                },
            ],
            products: [
                {
                    tag: 'Operate',
                    sub: 'Run your venue',
                    desc: 'Reservations, payments, memberships, kiosk, and waivers. Everything you need to open your doors and delight customers from day one.',
                    price: '$200 / venue / month',
                },
                {
                    tag: 'Launch',
                    sub: 'Connect your simulators',
                    desc: 'The on-sim launcher for Assetto Corsa. Customer-friendly interface, kiosk mode, remote race control, and Race Ready — car and track loaded before the customer sits down.',
                    price: '1× session price / sim / month',
                },
                {
                    tag: 'Engage',
                    sub: 'Grow your customer base',
                    desc: 'Email and SMS campaigns, win-back automations, review requests, and social scheduling — all connected to your ShiftOS customer data.',
                    price: '$100 / venue / month',
                },
            ],
            software: ['Assetto Corsa', 'AC Content Manager', 'CSP + Pure', 'SimHub', 'CrewChief', 'iRacing', 'AC Competizione', 'rFactor 2', 'Le Mans Ultimate', 'Automobilista 2', 'RaceRoom Experience', 'OBS Studio', 'Trading Paints', 'Fanalab', 'Moza Pit House', 'Most other software'],
            hardware: ['Simucube', 'D-Box Motion', 'Sigma Integrale', 'Heusinkveld', 'Fanatec Pedals', 'Fanatec Wheelbases', 'Moza Racing',  'Bass Shakers', 'Button Boxes', 'Triple Monitors', 'Nvidia Surround', 'VR Headsets', 'Load Cell Pedals', 'Direct Drive Wheels', 'Most other hardware'],
            form: {
                name: '',
                user_provided_email: '',
                ticket_name: '',
                product: '',
                is_shiftos_customer: null,
                issue: '',
            },
            productOptions: [],
            loading: false,
            success: false,
            error: null,
        }
    },
    computed: {
        isFormValid() {
            const base = this.form.user_provided_email.trim() && this.form.ticket_name.trim() && this.form.product !== '' && this.form.is_shiftos_customer !== null && this.form.issue.trim()
            if (this.form.is_shiftos_customer === false) return !!(base && this.form.name.trim())
            return !!base
        },
        productSelectOptions() {
            return this.productOptions.map((p) => ({ value: p.id, text: p.name }))
        },
        isBusinessHours() {
            const now = new Date()
            const pt = new Date(now.toLocaleString('en-US', { timeZone: businessHours.tzName }))
            const hour = pt.getHours()
            const day = pt.getDay()
            return businessHours.activeDays.includes(day) && hour >= businessHours.startHour && hour < businessHours.endHour
        },
    },
    async mounted() {
        try {
            const res = await fetch(`${API_URL}/support/products`)
            if (res.ok) this.productOptions = await res.json()
        } catch (e) {
            // non-fatal — form remains usable
        }
    },
    methods: {
        scrollTo(id) {
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ behavior: 'smooth' })
        },
        async submitTicket() {
            if (!this.isFormValid || this.loading) return
            this.loading = true
            this.error = null
            const payload = {
                name: this.form.ticket_name.trim(),
                user_provided_email: this.form.user_provided_email.trim(),
                product: this.form.product,
                is_shiftos_customer: this.form.is_shiftos_customer,
                issue: this.form.issue.trim(),
            }
            if (this.form.is_shiftos_customer === false) payload.contact_name = this.form.name.trim()
            try {
                const res = await fetch(`${API_URL}/support/tickets`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                })
                if (!res.ok) throw new Error(`Server responded with ${res.status}`)
                this.success = true
                this.resetForm()
            } catch (err) {
                this.error = 'Something went wrong submitting your ticket. Please try again, or email us directly.'
            } finally {
                this.loading = false
            }
        },
        resetForm() {
            this.form = { name: '', user_provided_email: '', ticket_name: '', product: '', is_shiftos_customer: null, issue: '' }
        },
        submitAnother() {
            this.success = false
            this.error = null
        },
    },
}
</script>

<template>
    <div class="home">
        <nav-component />

        <!-- ═══════════════════════════════════ HERO ══════════════════════════════════ -->
        <section class="hero">
            <div class="hero-track">
                <div class="hero-track-grid"></div>
            </div>
            <div class="hero-overlay"></div>
            <div class="hero-container">
                <div class="hero-content">
                    <h1>Expert support for the<br><span class="hero-accent">sim racing world.</span></h1>
                    <p class="lead hero-lead">From ShiftOS onboarding to wheelbase tuning and Assetto Corsa optimization — we cover the full sim racing stack. Software, hardware, and everything in between.</p>
                    <div class="hero-actions">
                        <b-button class="btn-red btn-lg" @click="scrollTo('ticket')">Submit a Ticket <span class="arrow">→</span></b-button>
                        <b-button class="btn-ghost btn-lg" @click="scrollTo('services')">See our services</b-button>
                    </div>
                    <div class="hero-guarantee">
                        <i class="ph-duotone ph-clock"></i>
                        <span>Response within <strong>1 hour</strong> · <hours /></span>
                    </div>
                </div>
            </div>
        </section>

        <!-- ═══════════════════════════════ SERVICES ══════════════════════════════════ -->
        <section class="section services-section" id="services">
            <div class="container">
                <div class="section-header centered">
                    <h2>Support that covers the whole grid.</h2>
                    <p class="lead">Whether you're a ShiftOS venue, a Shift Arcade franchisee, or an individual racer chasing tenths — we have a support tier for you.</p>
                </div>
                <b-row class="services-row">
                    <b-col v-for="(svc, i) in services" :key="i" cols="12" lg="4" class="mb-4 mb-lg-0 d-flex">
                        <div class="service-card w-100">
                            <div class="service-card-top">
                                <div class="service-icon-row">
                                    <div class="service-icon"><i :class="svc.icon"></i></div>
                                </div>
                                <h3 class="svc-title">{{ svc.title }}</h3>
                                <p class="svc-desc">{{ svc.desc }}</p>
                            </div>
                            <ul class="svc-features">
                                <li v-for="(f, j) in svc.features" :key="j"><span class="check">✓</span><span>{{ f }}</span></li>
                            </ul>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </section>

        <!-- ═══════════════════════════════ SHIFTOS ═══════════════════════════════════ -->
        <section class="section shiftos-section" id="shiftos">
            <div class="container">
                <b-row align-v="center" class="shiftos-inner">
                    <b-col cols="12" lg="6" class="shiftos-left mb-4 mb-lg-0">
                        <img src="@/assets/images/shiftos-logo-web.png" height="30" />
                        <p>&nbsp;</p>
                        <h2>The complete platform for sim racing venues.</h2>
                        <p class="lead">Bookings, payments, kiosk check-in, race control, memberships, and CRM. ShiftOS is what powers the world's best sim racing lounges — and our support team knows it inside out.</p>
                        <div class="shiftos-stats">
                            <div class="shiftos-stat"><div class="stat-num">3</div><div class="stat-lbl">integrated products</div></div>
                            <div class="stat-div"></div>
                            <div class="shiftos-stat"><div class="stat-num">$0</div><div class="stat-lbl">license fee to start</div></div>
                            <div class="stat-div"></div>
                            <div class="shiftos-stat"><div class="stat-num">900%+</div><div class="stat-lbl">average ROI</div></div>
                        </div>
                        <b-button tag="a" href="https://shiftos.app" rel="noopener" class="btn-red">Learn about ShiftOS <span class="arrow">→</span></b-button>
                    </b-col>
                    <b-col cols="12" lg="6" class="shiftos-right">
                        <div class="products-stack">
                            <div v-for="(p, i) in products" :key="i" class="product-card">
                                <div class="product-card-header">
                                    <div class="product-tag">{{ p.tag }}</div>
                                    <div class="product-sub">{{ p.sub }}</div>
                                </div>
                                <p class="product-desc">{{ p.desc }}</p>
                                <div class="product-price">{{ p.price }}</div>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </section>

        <!-- ═══════════════════════════════ SUPPORTED ═════════════════════════════════ -->
        <section class="section supported-section" id="supported">
            <div class="container">
                <div class="section-header centered">
                    <span class="overline">What We Support</span>
                    <h2>If it's on your rig, we can fix it.</h2>
                    <p class="lead">Our team works with the full spectrum of sim racing software and hardware every day. Whatever you're running, we've seen it before.</p>
                </div>
                <b-row class="supported-cols">
                    <b-col cols="12" md="6" class="mb-3 mb-md-0">
                        <div class="supported-col">
                            <div class="col-label"><i class="ph-duotone ph-desktop"></i> Software & Sims</div>
                            <div class="tag-grid">
                                <span v-for="(item, i) in software" :key="i" class="tag-item">{{ item }}</span>
                            </div>
                        </div>
                    </b-col>
                    <b-col cols="12" md="6">
                        <div class="supported-col">
                            <div class="col-label"><i class="ph-duotone ph-wrench"></i> Hardware & Peripherals</div>
                            <div class="tag-grid">
                                <span v-for="(item, i) in hardware" :key="i" class="tag-item">{{ item }}</span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <div class="supported-note">
                    <i class="ph-duotone ph-info"></i>
                    <span>Don't see your hardware or software listed? If it's sim racing related, we likely support it. <strong role="button" @click="scrollTo('ticket')">Submit a ticket</strong> and ask.</span>
                </div>
            </div>
        </section>

        <!-- ════════════════════════════════ TICKET ═══════════════════════════════════ -->
        <section class="ticket-section" id="ticket">
            <div class="ticket-bg"></div>
            <div class="container">
                <b-row class="ticket-inner">

                    <!-- Left: pitch -->
                    <b-col cols="12" lg="6" class="ticket-left mb-4 mb-lg-0">
                        <span class="overline">Support Ticket</span>
                        <h2 class="ticket-heading">We'll get you<br>back on track.</h2>
                        <p class="lead ticket-lead">Describe what's happening and we'll connect you with the right person. No bots, no queues — just real help from real sim racers.</p>

                        <div class="guarantee-box">
                            <div class="guarantee-row">
                                <i class="ph-duotone ph-clock"></i>
                                <div>
                                    <div class="guarantee-title">Response within 1 hour</div>
                                    <div class="guarantee-sub">If submitted <hours /></div>
                                </div>
                                <div :class="['hours-pill', isBusinessHours ? 'pill-open' : 'pill-closed']">
                                    {{ isBusinessHours ? 'Open now' : 'After hours' }}
                                </div>
                            </div>
                            <div class="guarantee-row guarantee-row-2">
                                <i class="ph-duotone ph-envelope"></i>
                                <div>
                                    <div class="guarantee-title">Outside business hours?</div>
                                    <div class="guarantee-sub">We'll reply first thing next business day.</div>
                                </div>
                            </div>
                        </div>

                        <div class="ticket-tags-wrap">
                            <div class="ticket-tags-label">Ticket types we handle:</div>
                            <div class="ticket-tags">
                                <span class="ttag">ShiftOS support</span>
                                <span class="ttag">Software setup</span>
                                <span class="ttag">Hardware config</span>
                                <span class="ttag">iRacing / Assetto Corsa</span>
                                <span class="ttag">SimHub & CrewChief</span>
                                <span class="ttag">Wheelbase tuning</span>
                                <span class="ttag">Motion platforms</span>
                                <span class="ttag">General sim help</span>
                            </div>
                        </div>
                    </b-col>

                    <!-- Right: form -->
                    <b-col cols="12" lg="6" class="ticket-right">

                        <!-- Success -->
                        <div v-if="success" class="form-card form-success">
                            <div class="success-icon"><i class="ph-duotone ph-check-circle"></i></div>
                            <h3>Ticket submitted!</h3>
                            <p>{{ isBusinessHours ? "We'll respond within 1 hour." : "We'll respond first thing next business day." }} Keep an eye on your inbox.</p>
                            <b-button class="btn-outline-dark" @click="submitAnother">Submit another ticket</b-button>
                        </div>

                        <!-- Form -->
                        <div v-else class="form-card">
                            <div class="form-card-header">
                                <h3>New Support Ticket</h3>
                                <p>All fields are required.</p>
                            </div>

                            <b-form @submit.prevent="submitTicket" novalidate>
                                <b-form-group label="Are you a ShiftOS customer?" label-class="field-label">
                                    <div class="radio-group">
                                        <label class="radio-opt" :class="{ selected: form.is_shiftos_customer === false }">
                                            <input type="radio" :value="false" v-model="form.is_shiftos_customer" :disabled="loading" />
                                            <span>No — I am not a ShiftOS customer</span>
                                        </label>
                                        <label class="radio-opt" :class="{ selected: form.is_shiftos_customer === true }">
                                            <input type="radio" :value="true" v-model="form.is_shiftos_customer" :disabled="loading" />
                                            <span>Yes — I am a ShiftOS customer</span>
                                        </label>
                                    </div>
                                </b-form-group>

                                <div v-if="form.is_shiftos_customer === true" class="shiftos-ticket-alert">
                                    <i class="ph-duotone ph-info"></i>
                                    <div>
                                        <strong>Get faster support in ShiftOS.</strong> As a customer, you can <a href="https://panel.shiftos.app/admin/support/ticket/add/" target="_blank" rel="noopener">create a ticket directly in your ShiftOS panel</a> — where you can attach screenshots, add full detail, and track progress in one place. Or continue below to submit here.
                                    </div>
                                </div>

                                <b-form-group v-if="form.is_shiftos_customer !== true" label="Your name" label-class="field-label" label-for="f-name">
                                    <b-form-input id="f-name" v-model="form.name" type="text" placeholder="First and last name" class="field-input" :disabled="loading" required />
                                </b-form-group>

                                <b-form-group label="Email address" label-class="field-label" label-for="f-email" :description="form.is_shiftos_customer === true ? 'Use the email address you log in to ShiftOS with' : ''">
                                    <b-form-input id="f-email" v-model="form.user_provided_email" type="email" placeholder="you@example.com" class="field-input" :disabled="loading" required />
                                </b-form-group>

                                <b-form-group label="Product you need help with" label-class="field-label" label-for="f-product">
                                    <b-form-select id="f-product" v-model="form.product" :options="productSelectOptions" class="field-input" :disabled="loading" required>
                                        <template #first>
                                            <b-form-select-option value="" disabled>Select a product…</b-form-select-option>
                                        </template>
                                    </b-form-select>
                                </b-form-group>

                                <b-form-group label="Summary" label-class="field-label" label-for="f-ticket-name" description="Brief summary of the issue — 75 characters or fewer">
                                    <b-form-input id="f-ticket-name" v-model="form.ticket_name" type="text" placeholder="e.g. SimHub not detecting my bass shakers" class="field-input" :maxlength="75" :disabled="loading" required />
                                </b-form-group>

                                <b-form-group label="Describe your issue" label-class="field-label" label-for="f-issue" description="The more detail you provide, the faster we can help.">
                                    <b-form-textarea id="f-issue" v-model="form.issue" placeholder="What's happening? What have you already tried? Any error messages?" class="field-input field-textarea" rows="5" :disabled="loading" required />
                                </b-form-group>

                                <div v-if="error" class="form-error">
                                    <i class="ph ph-warning-circle"></i> {{ error }}
                                </div>

                                <b-button type="submit" class="btn-red btn-submit" :disabled="!isFormValid || loading">
                                    <span v-if="loading"><i class="ph ph-circle-notch spin"></i> Submitting…</span>
                                    <span v-else>Submit Ticket <span class="arrow">→</span></span>
                                </b-button>

                                <div class="form-fine-print">
                                    <i class="ph-duotone ph-clock"></i>
                                    Responses within 1 hour, <hours /> · Outside those hours, expect a reply first thing next business day.
                                </div>
                            </b-form>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </section>

        <footer-component />
    </div>
</template>

<style scoped>
/* ── SHARED ─────────────────────────────────────────────────── */
.home {
    min-height: 100vh;
}

.container {
    max-width: 1140px;
    margin: 0 auto;
    padding: 0 28px;
}

.section {
    padding: 96px 0;
}

.overline {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: #e8201a;
    margin-bottom: 14px;
    font-family: 'Inter', system-ui, sans-serif;
}

.lead {
    font-size: 18px;
    color: #6b7280;
    line-height: 1.65;
    font-family: 'Inter', system-ui, sans-serif;
}

.section-header {
    margin-bottom: 52px;
}

.section-header h2 {
    margin-bottom: 14px;
}

.section-header.centered {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
}

/* ── BUTTONS ────────────────────────────────────────────────── */
.btn-red {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 24px;
    background: #e8201a;
    color: #fff;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s;
    white-space: nowrap;
    border: none;
    font-family: 'Inter', system-ui, sans-serif;
    text-decoration: none;
}

.btn-red:hover {
    background: #c01814;
    transform: translateY(-1px);
    color: #fff;
    text-decoration: none;
}

.btn-red:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.btn-lg {
    padding: 16px 32px;
    font-size: 16px;
}

.btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    background: transparent;
    color: #ffffff;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    white-space: nowrap;
    border: 1px solid rgba(255, 255, 255, 0.22);
    font-family: 'Inter', system-ui, sans-serif;
}

.btn-ghost:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.38);
    color: #ffffff;
}

.btn-outline-dark {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    background: transparent;
    color: #374151;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    border: 1px solid #d1d5db;
    font-family: 'Inter', system-ui, sans-serif;
}

.btn-outline-dark:hover {
    border-color: #9ca3af;
    background: #f5f7fa;
    color: #374151;
}

.arrow {
    transition: transform 0.15s;
}

.btn-red:hover .arrow,
.btn-ghost:hover .arrow {
    transform: translateX(3px);
}

/* ── HERO ───────────────────────────────────────────────────── */
.hero {
    position: relative;
    padding: 130px 0 110px;
    overflow: hidden;
    background: #040404;
    background-image:
        linear-gradient(108deg, rgba(4, 4, 4, 0.99) 0%, rgba(4, 4, 4, 0.95) 32%, rgba(4, 4, 4, 0.72) 58%, rgba(4, 4, 4, 0.12) 100%),
        radial-gradient(ellipse 680px 480px at 84% 46%, rgba(232, 32, 26, 0.52) 0%, rgba(200, 24, 20, 0.22) 28%, transparent 60%),
        radial-gradient(ellipse 420px 280px at 92% 12%, rgba(232, 32, 26, 0.3) 0%, transparent 55%),
        radial-gradient(ellipse 380px 260px at 100% 88%, rgba(180, 20, 15, 0.22) 0%, transparent 55%),
        repeating-linear-gradient(-14deg, transparent 0, transparent 30px, rgba(255, 255, 255, 0.022) 30px, rgba(255, 255, 255, 0.022) 31px),
        radial-gradient(ellipse 1400px 900px at 88% 50%, #130407 0%, #040404 68%);
    background-size: cover;
}

.hero-track {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 62%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
}

.hero-track-grid {
    position: absolute;
    bottom: -8%;
    left: 50%;
    transform: translateX(-50%) perspective(380px) rotateX(32deg);
    width: 280%;
    height: 160%;
    background-image:
        repeating-linear-gradient(transparent, transparent 47px, rgba(255, 255, 255, 0.075) 47px, rgba(255, 255, 255, 0.075) 48px),
        repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(255, 255, 255, 0.06) 47px, rgba(255, 255, 255, 0.06) 48px);
    -webkit-mask-image: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.4) 22%, rgba(0, 0, 0, 0.08) 52%, transparent 72%);
    mask-image: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.4) 22%, rgba(0, 0, 0, 0.08) 52%, transparent 72%);
}

.hero-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
        repeating-linear-gradient(0deg, transparent 0, transparent 2px, rgba(255, 255, 255, 0.011) 2px, rgba(255, 255, 255, 0.011) 3px);
    z-index: 0;
}

.hero-container {
    position: relative;
    z-index: 1;
    max-width: 1140px;
    margin: 0 auto;
    padding: 0 28px;
}

.hero h1 {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: clamp(52px, 7vw, 84px);
    font-weight: 700;
    line-height: 0.97;
    letter-spacing: -1px;
    color: #ffffff;
    margin-bottom: 24px;
}

.hero-accent {
    color: #e8201a;
}

.hero-lead {
    max-width: 540px;
    margin-bottom: 40px;
    color: rgba(255, 255, 255, 0.58);
}

.hero-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 40px;
}

.hero-guarantee {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 18px;
    background: rgba(16, 185, 129, 0.09);
    border: 1px solid rgba(16, 185, 129, 0.22);
    border-radius: 8px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);
    font-family: 'Inter', system-ui, sans-serif;
}

.hero-guarantee i {
    font-size: 18px;
    color: #10b981;
    flex-shrink: 0;
}

.hero-guarantee strong {
    color: #10b981;
}

/* ── SERVICES ───────────────────────────────────────────────── */
.services-section {
    background: #f5f7fa;
    border-top: 1px solid #e5e7eb;
}

.service-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.service-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.07);
    transform: translateY(-3px);
}

.service-card-top {
    flex: 1;
}

.service-icon-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
}

.service-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    background: rgba(232, 32, 26, 0.08);
    border: 1px solid rgba(232, 32, 26, 0.2);
    color: #e8201a;
    flex-shrink: 0;
}

.svc-title {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 10px;
    line-height: 1.2;
}

.svc-desc {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.65;
    font-family: 'Inter', system-ui, sans-serif;
    margin: 0;
}

.svc-features {
    list-style: none;
    padding: 0;
    margin: 0;
    border-top: 1px solid #e5e7eb;
    padding-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.svc-features li {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 13px;
    color: #374151;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.4;
}

.check {
    color: #10b981;
    font-weight: 700;
    flex-shrink: 0;
}

/* ── SHIFTOS ────────────────────────────────────────────────── */
.shiftos-section {
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
}

.shiftos-left h2 {
    margin-bottom: 16px;
}

.shiftos-left .lead {
    margin-bottom: 32px;
}

.shiftos-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 32px;
    padding: 20px 24px;
    background: #f5f7fa;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
}

.shiftos-stat .stat-num {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #111827;
    line-height: 1;
}

.stat-lbl {
    font-size: 11px;
    color: #6b7280;
    margin-top: 3px;
    font-family: 'Inter', system-ui, sans-serif;
}

.stat-div {
    width: 1px;
    height: 36px;
    background: #e5e7eb;
    flex-shrink: 0;
}

.products-stack {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.product-card {
    background: #f5f7fa;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px 22px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.product-card:hover {
    border-color: rgba(232, 32, 26, 0.35);
    box-shadow: 0 4px 16px rgba(232, 32, 26, 0.06);
}

.product-card-header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 8px;
}

.product-tag {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
}

.product-sub {
    font-size: 12px;
    font-weight: 600;
    color: #e8201a;
    font-family: 'Inter', system-ui, sans-serif;
}

.product-desc {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 10px;
    font-family: 'Inter', system-ui, sans-serif;
}

.product-price {
    font-size: 12px;
    font-weight: 600;
    color: #9ca3af;
    font-family: 'Inter', system-ui, sans-serif;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
}

/* ── SUPPORTED ──────────────────────────────────────────────── */
.supported-section {
    background: #f5f7fa;
    border-top: 1px solid #e5e7eb;
}

.supported-cols {
    margin-bottom: 24px;
}

.supported-col {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 28px;
    height: 100%;
}

.col-label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #6b7280;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
    font-family: 'Inter', system-ui, sans-serif;
}

.col-label i {
    font-size: 20px;
    color: #e8201a;
}

.tag-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-item {
    display: inline-block;
    padding: 6px 12px;
    background: #f5f7fa;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    transition: border-color 0.15s, color 0.15s;
    font-family: 'Inter', system-ui, sans-serif;
    cursor: default;
}

.tag-item:hover {
    border-color: rgba(232, 32, 26, 0.3);
    color: #111827;
}

.supported-note {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
    background: rgba(232, 32, 26, 0.04);
    border: 1px solid rgba(232, 32, 26, 0.14);
    border-radius: 10px;
    font-size: 13px;
    color: #6b7280;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.5;
}

.supported-note i {
    font-size: 20px;
    color: #e8201a;
    flex-shrink: 0;
    margin-top: 1px;
}

.supported-note strong {
    color: #e8201a;
}

/* ── TICKET ─────────────────────────────────────────────────── */
.ticket-section {
    position: relative;
    padding: 96px 0;
    background: #040404;
    overflow: hidden;
}

.ticket-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(ellipse 700px 500px at 0% 50%, rgba(232, 32, 26, 0.1) 0%, transparent 70%), radial-gradient(ellipse 500px 400px at 100% 80%, rgba(232, 32, 26, 0.07) 0%, transparent 70%);
    z-index: 0;
}

.ticket-section .container {
    position: relative;
    z-index: 1;
}

.ticket-heading {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: clamp(36px, 4vw, 52px);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.5px;
    color: #ffffff;
    margin-bottom: 16px;
}

.ticket-lead {
    color: rgba(255, 255, 255, 0.52);
    margin-bottom: 28px;
}

.guarantee-box {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 28px;
}

.guarantee-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 20px;
}

.guarantee-row-2 {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.guarantee-row i {
    font-size: 22px;
    color: #10b981;
    flex-shrink: 0;
    margin-top: 1px;
}

.guarantee-title {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 3px;
    font-family: 'Inter', system-ui, sans-serif;
}

.guarantee-sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.38);
    font-family: 'Inter', system-ui, sans-serif;
}

.hours-pill {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 20px;
    flex-shrink: 0;
    font-family: 'Inter', system-ui, sans-serif;
    align-self: center;
}

.pill-open {
    background: rgba(16, 185, 129, 0.14);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.28);
}

.pill-closed {
    background: rgba(156, 163, 175, 0.14);
    color: #9ca3af;
    border: 1px solid rgba(156, 163, 175, 0.28);
}

.ticket-tags-wrap {
    margin-top: 4px;
}

.ticket-tags-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.28);
    margin-bottom: 12px;
    font-family: 'Inter', system-ui, sans-serif;
}

.ticket-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ttag {
    display: inline-block;
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.36);
    font-family: 'Inter', system-ui, sans-serif;
}

/* Right — form card */
.form-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
}

.form-card-header {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
}

.form-card-header h3 {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 4px;
}

.form-card-header p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
    font-family: 'Inter', system-ui, sans-serif;
}

/* Bootstrap form overrides */
:deep(.field-label) {
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #374151 !important;
    margin-bottom: 6px !important;
    font-family: 'Inter', system-ui, sans-serif !important;
}

:deep(.field-input) {
    font-family: 'Inter', system-ui, sans-serif !important;
    font-size: 14px !important;
    color: #111827 !important;
    background: #ffffff !important;
    border: 1px solid #d1d5db !important;
    border-radius: 8px !important;
    padding: 8x 14px !important;
    transition: border-color 0.15s, box-shadow 0.15s !important;
    box-shadow: none !important;
}

:deep(.field-input:focus) {
    border-color: #e8201a !important;
    box-shadow: 0 0 0 3px rgba(232, 32, 26, 0.1) !important;
    outline: none !important;
}

:deep(.field-input::placeholder) {
    color: #9ca3af !important;
}

:deep(.field-input:disabled) {
    background: #f9fafb !important;
    color: #9ca3af !important;
}

:deep(.field-textarea) {
    resize: vertical;
    min-height: 120px;
}

:deep(.form-text) {
    font-size: 12px !important;
    color: #9ca3af !important;
    font-family: 'Inter', system-ui, sans-serif !important;
    margin-top: 4px !important;
}

:deep(.form-group) {
    margin-bottom: 18px !important;
}

/* ShiftOS customer info alert */
.shiftos-ticket-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    background: rgba(59, 130, 246, 0.06);
    border: 1px solid rgba(59, 130, 246, 0.22);
    border-radius: 10px;
    font-size: 13px;
    color: #374151;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.55;
    margin-bottom: 18px;
}

.shiftos-ticket-alert i {
    font-size: 20px;
    color: #3b82f6;
    flex-shrink: 0;
    margin-top: 1px;
}

.shiftos-ticket-alert a {
    color: #2563eb;
    font-weight: 600;
    text-decoration: underline;
}

.shiftos-ticket-alert a:hover {
    color: #1d4ed8;
}

/* Radio */
.radio-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.radio-opt {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    margin-bottom: 0;
    font-weight: normal;
}

.radio-opt:hover {
    border-color: rgba(232, 32, 26, 0.3);
}

.radio-opt.selected {
    border-color: #e8201a;
    background: rgba(232, 32, 26, 0.04);
}

.radio-opt input[type='radio'] {
    accent-color: #e8201a;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
}

.radio-opt span {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    font-family: 'Inter', system-ui, sans-serif;
}

.radio-opt.selected span {
    color: #111827;
}

/* Error */
.form-error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    background: rgba(239, 68, 68, 0.06);
    border: 1px solid rgba(239, 68, 68, 0.22);
    border-radius: 8px;
    font-size: 13px;
    color: #dc2626;
    margin-bottom: 16px;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.5;
}

.form-error i {
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 1px;
}

/* Submit */
.btn-submit {
    width: 100%;
    justify-content: center;
    font-size: 15px;
    padding: 14px;
    margin-top: 4px;
}

.form-fine-print {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 14px;
    font-size: 12px;
    color: #9ca3af;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.5;
}

.form-fine-print i {
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 1px;
}

/* Success */
.form-success {
    text-align: center;
    padding: 48px 32px;
}

.success-icon {
    font-size: 56px;
    color: #10b981;
    margin-bottom: 16px;
    line-height: 1;
}

.form-success h3 {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 12px;
}

.form-success p {
    font-size: 15px;
    color: #6b7280;
    line-height: 1.65;
    max-width: 340px;
    margin: 0 auto 28px;
    font-family: 'Inter', system-ui, sans-serif;
}

/* Spinner */
.spin {
    animation: spin 1s linear infinite;
    display: inline-block;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* ── BOOTSTRAP GRID GAPS ─────────────────────────────────────── */
@media (min-width: 992px) {
    .shiftos-left {
        padding-right: 36px;
    }

    .shiftos-right {
        padding-left: 36px;
    }

    .ticket-left {
        padding-right: 36px;
    }

    .ticket-right {
        padding-left: 36px;
    }
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 991px) {
    .hero-track {
        width: 100%;
        opacity: 0.6;
    }

    .services-row .service-card {
        max-width: 520px;
        margin-left: auto;
        margin-right: auto;
    }
}

@media (max-width: 600px) {
    .hero {
        padding: 88px 0 72px;
    }

    .hero h1 {
        font-size: 46px;
    }

    .section {
        padding: 72px 0;
    }
}
</style>
